import { AnalyticsTrackingEvent } from "./events";
import { BrandclubEventType, TrackingPixel } from "./types";

export const timeoutPromise = <T>(ms: number, name: string, fn: () => Promise<T>) => {
  return new Promise<T>((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`${name} timed out after ${ms}ms`));
    }, ms);
    fn().then(resolve, reject);
  });
};

function mapAppEventToExternalCustomEvent(bcEventName: string, supportedEventsConfig: TrackingPixel['eventMapping']) {
  if (!supportedEventsConfig) {
    return undefined;
  }

  // Page View events
  if (supportedEventsConfig.pageView?.enabled &&
    (bcEventName?.endsWith('page view') || bcEventName?.endsWith('bc_checkout_page_view'))) {
    return {
      eventName: supportedEventsConfig.pageView.mappedEventName,
      eventType: BrandclubEventType.PageView,
    };
  }

  // Navigate to Retailer events
  if (supportedEventsConfig.navigateToRetailer?.enabled &&
    bcEventName?.endsWith('openRetailerProductPage')) {
    return {
      eventName: supportedEventsConfig.navigateToRetailer.mappedEventName,
      eventType: BrandclubEventType.NavigateToRetailer,
    };
  }

  // Add to Cart events
  if (supportedEventsConfig.addToCart?.enabled &&
    bcEventName?.endsWith(':addToCart')) {
    return {
      eventName: supportedEventsConfig.addToCart.mappedEventName,
      eventType: BrandclubEventType.AddToCart,
    };
  }

  // View Cart / View Bag events
  if (supportedEventsConfig.viewCart?.enabled &&
    (bcEventName === AnalyticsTrackingEvent.DTC_OPEN_BUYBOX
      || bcEventName === AnalyticsTrackingEvent.VIEW_BAG
      || bcEventName === AnalyticsTrackingEvent.BC_CHECKOUT_OPEN_BAG_PAGE)) {
    return {
      eventName: supportedEventsConfig.viewCart.mappedEventName,
      eventType: BrandclubEventType.ViewCart,
    };
  }

  // Initiate Checkout events - // triggered before landing on the checkout page
  if (supportedEventsConfig.initiateCheckout?.enabled &&
    (bcEventName === AnalyticsTrackingEvent.INITIATE_CHECKOUT
      || bcEventName === AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT)) {
    return {
      eventName: supportedEventsConfig.initiateCheckout.mappedEventName,
      eventType: BrandclubEventType.InitiateCheckout,
    };
  }

  // Order Confirmation events // confirm the order details before placing the order on the checkout page
  if (supportedEventsConfig.orderConfirmation?.enabled &&
    (bcEventName === AnalyticsTrackingEvent.CHECKOUT_INTENT_CREATED)) {
    return {
      eventName: supportedEventsConfig.orderConfirmation.mappedEventName,
      eventType: BrandclubEventType.OrderConfirmation,
    };
  }

  // Order Submitted events // triggered after the order is placed on the checkout page
  if (supportedEventsConfig.orderSubmitted?.enabled &&
    (bcEventName === AnalyticsTrackingEvent.CHECKOUT_ORDER_SUBMIT)) {
    return {
      eventName: supportedEventsConfig.orderSubmitted.mappedEventName,
      eventType: BrandclubEventType.OrderSubmitted,
    };
  }

  // Purchase Confirmed events // triggered on the purchase confirmation page
  if (supportedEventsConfig.purchaseConfirmed?.enabled &&
    (bcEventName === AnalyticsTrackingEvent.CHECKOUT_ORDER_SUCCESS)) {
    return {
      eventName: supportedEventsConfig.purchaseConfirmed.mappedEventName,
      eventType: BrandclubEventType.PurchaseConfirmed,
    };
  }

  return undefined;
}

export function parseEventAndPayload(eventName: string, payload: any, supportedEventsConfig: TrackingPixel['eventMapping']) {
  const mappedEvent = mapAppEventToExternalCustomEvent(eventName, supportedEventsConfig);

  if (!mappedEvent?.eventName || !mappedEvent?.eventType) {
    return {
      eventName: undefined,
      eventPayload: {},
    } as const;
  }

  const { eventName: externalEventName, eventType } = mappedEvent;
  const convertedPayload = JSON.parse(
    JSON.stringify({
      stacklineSku: payload?.sku || payload?.stacklineSku,
      retailerId: payload?.retailerId,
      retailerSku: payload?.retailerSku,
      retailPrice: payload?.retailPrice,
      pageId: payload?.pageId,
      pageType: payload?.pageType,
      brandId: payload?.brandId,
      brandName: payload?.brandName,
      categoryId: payload?.categoryId,
      categoryName: payload?.categoryName,
      subCategoryId: payload?.subCategoryId,
      subCategoryName: payload?.subCategoryName,
      jl_id: payload?.jl_id,
      jl_c: payload?.jl_c,
      jl_m: payload?.jl_m,
      jl_s: payload?.jl_s,
      cartItems: payload?.cartItems?.map((item: any) => (item && {
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        brandId: item.brandId,
        title: item.title || item.productName,
        brandName: item.brandName,
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        subCategoryId: item.subCategoryId,
        subCategoryName: item.subCategoryName,
        retailPrice: item.retailPrice,
        quantity: item.quantity,
      })).filter(Boolean),
      checkoutItems: payload?.checkoutItems?.map((item: any) => (item && {
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        brandId: item.brandId,
        title: item.title,
        brandName: item.brandName,
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        subCategoryId: item.subCategoryId,
        subCategoryName: item.subCategoryName,
        retailPrice: item.retailPrice,
        quantity: item.quantity,
      })).filter(Boolean),
      checkoutIntentId: payload?.checkoutIntentId,
    }),
  );
  return {
    eventName: externalEventName,
    eventType,
    eventPayload: convertedPayload,
  };
}
